import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { ulid } from 'ulid';
import { z } from 'zod';
import { useLive, useNow, useRuntime } from './context';
import { useHasModule, useRemote } from './useRemote';
import { ApiFailure } from '../api/errors';
import { formatClock, formatFreshness, formatMoney } from '../mirror/time';
import { ParcelList } from '../wire';
import type { Parcel, ParcelStatus } from '../wire';
import { useLocale, useT } from '../messages';
import type { Messages, T, UiLocale } from '../messages';

/**
 * Parcels — the merch side of the Orders pattern (fulfillment wave 3).
 *
 * A fulfilled order is worked by machines, not by a kitchen: `paid` is written
 * by a capture committing, `submitted` by the submission drain, `shipped` by a
 * shipment notice read back from the manufacturer. So this board has **no
 * verbs** — there is nothing a person legitimately moves by hand — and every
 * lane is a fact about where the parcel is. The one human action is the one
 * the design reserves for a human: re-arming a submission the drain gave up
 * on (`submit_failed`, `next_attempt_at` null), which is the only button here.
 *
 * `delivered` is not a lane. The manufacturer never says it and this system
 * does not talk to carriers, so an order sits at `shipped` until somebody
 * says otherwise (`recordFulfillmentTracking` argues it). A lane for a state
 * nothing writes would be an empty column with a promise in its heading.
 *
 * The attention list is exported on its own: the Tasks screen shows the same
 * stuck parcels beside the booking conflicts, because "did not go out" is an
 * owner's task whichever screen they open first.
 */

const LANES = [
  { status: 'paid', label: 'parcels.lanePaid' },
  { status: 'submitted', label: 'parcels.laneSubmitted' },
  { status: 'shipped', label: 'parcels.laneShipped' },
] as const satisfies readonly { status: ParcelStatus; label: keyof Messages }[];

interface Refusal {
  orderId: string;
  message: string;
}

/**
 * The read, once, for both screens. Everything in the fulfilled lane for this
 * tenant (the route's default), filtered on the tablet; a second request for
 * the stuck subset would be the same rows twice.
 */
export function useParcels(): ReturnType<typeof useRemote<{ parcels: Parcel[] }>> {
  const runtime = useRuntime();
  const entitled = useHasModule('fulfillment');
  return useRemote(
    (signal) =>
      runtime.api.request({
        method: 'GET',
        path: '/fulfillment/parcels',
        query: { limit: 200 },
        schema: ParcelList,
        signal,
      }),
    [],
    {
      // `moveOrder` emits `order.updated` on every status change and the
      // submission machinery emits `fulfillment.*`; the poll backs a dead
      // stream.
      cue: (type) => type.startsWith('order.') || type.startsWith('fulfillment.'),
      pollMs: 15_000,
      enabled: entitled,
    },
  );
}

/** Re-arm a stuck submission: the one thing a person does on this board. */
export function useParcelRetry(onDone: () => void): {
  retry: (parcel: Parcel) => Promise<void>;
  busy: string | null;
  refusal: Refusal | null;
} {
  const runtime = useRuntime();
  const [busy, setBusy] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  const retry = useCallback(
    async (parcel: Parcel): Promise<void> => {
      setBusy(parcel.order_id);
      setRefusal(null);
      try {
        await runtime.api.request({
          method: 'POST',
          path: `/orders/${parcel.order_id}/fulfillment/submit`,
          body: {},
          idempotencyKey: ulid(),
          schema: z.object({ attempted: z.number(), submitted: z.number() }).passthrough(),
        });
      } catch (e) {
        // The server's sentence, verbatim — a `409 not_queued` says exactly
        // why this order cannot be re-armed (it already went, or it was never
        // paid), and that is the one thing this screen does not know.
        if (e instanceof ApiFailure || e instanceof Error) {
          setRefusal({ orderId: parcel.order_id, message: e.message });
        }
      } finally {
        setBusy(null);
        onDone();
      }
    },
    [runtime, onDone],
  );

  return { retry, busy, refusal };
}

/** The one other thing a person does here: say the parcel arrived. */
export function useParcelDelivered(onDone: () => void): {
  deliver: (parcel: Parcel) => Promise<void>;
  busy: string | null;
  refusal: Refusal | null;
} {
  const runtime = useRuntime();
  const [busy, setBusy] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const deliver = useCallback(
    async (parcel: Parcel): Promise<void> => {
      setBusy(parcel.order_id);
      setRefusal(null);
      try {
        await runtime.api.request({
          method: 'POST',
          path: `/orders/${parcel.order_id}/fulfillment/delivered`,
          body: {},
          idempotencyKey: ulid(),
          schema: z.object({ order_id: z.string() }).passthrough(),
        });
      } catch (e) {
        if (e instanceof ApiFailure || e instanceof Error) {
          setRefusal({ orderId: parcel.order_id, message: e.message });
        }
      } finally {
        setBusy(null);
        onDone();
      }
    },
    [runtime, onDone],
  );
  return { deliver, busy, refusal };
}

export function parcelsNeedingAttention(parcels: readonly Parcel[]): Parcel[] {
  return parcels.filter((parcel) => parcel.status === 'submit_failed');
}

/**
 * The stuck parcels, as cards with the one button. Shared by this board and
 * by Tasks.
 */
export function ParcelAttention({
  parcels,
  retry,
  busy,
  refusal,
  currency,
  timezone,
  t,
  locale,
}: {
  parcels: readonly Parcel[];
  retry: (parcel: Parcel) => Promise<void>;
  busy: string | null;
  refusal: Refusal | null;
  currency: string;
  timezone: string;
  t: T;
  locale: UiLocale;
}): ReactNode {
  if (parcels.length === 0) return null;
  return (
    <ul className="task-list task-list--parcels">
      {parcels.map((parcel) => (
        <li key={parcel.order_id} className="task task--parcel">
          <h2>{t('parcels.stuckTitle', { name: parcel.recipient.name })}</h2>
          <p className="task__message">
            {t('parcels.stuckBody', {
              attempts: parcel.attempts,
              total: formatMoney(parcel.total_cents, currency, locale),
              clock: formatClock(parcel.placed_at, timezone, locale),
            })}
          </p>
          {parcel.last_error === null ? null : (
            <p className="task__error muted">
              {t('parcels.lastError', { error: parcel.last_error })}
            </p>
          )}
          {refusal !== null && refusal.orderId === parcel.order_id ? (
            <p className="error" role="alert">
              {refusal.message}
            </p>
          ) : null}
          <div className="task__actions">
            <button
              type="button"
              className="button button--primary"
              disabled={busy === parcel.order_id}
              onClick={() => void retry(parcel)}
            >
              {t('parcels.retry')}
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}

export function Parcels(): ReactNode {
  const runtime = useRuntime();
  useNow();
  const now = runtime.now();
  const t = useT();
  const locale = useLocale();

  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';
  const currency = session?.tenant.currency ?? 'USD';

  const entitled = useHasModule('fulfillment');
  const parcels = useParcels();
  const rows = parcels.data?.parcels ?? [];
  const stuck = parcelsNeedingAttention(rows);
  const { retry, busy, refusal } = useParcelRetry(parcels.reload);
  const delivered = useParcelDelivered(parcels.reload);
  const arrived = rows.filter((parcel) => parcel.status === 'delivered');

  if (!entitled) {
    return (
      <section className="view">
        <p className="empty">{t('parcels.notSwitchedOn')}</p>
      </section>
    );
  }

  return (
    <section className="view view--parcels">
      <div className="view__header">
        <h1>{t('parcels.title')}</h1>
        <span className="status-strip__staleness">
          {`${formatFreshness(parcels.fetchedAt, now, timezone, locale)}${
            parcels.error === null ? '' : t('parcels.couldNotReach')
          }`}
        </span>
      </div>

      {parcels.loading && parcels.fetchedAt === null ? (
        <p className="empty">{t('parcels.reading')}</p>
      ) : null}

      {stuck.length === 0 ? null : (
        <article className="panel panel--warn">
          <h2>{t('parcels.stuckHeading', { count: stuck.length })}</h2>
          <ParcelAttention
            parcels={stuck}
            retry={retry}
            busy={busy}
            refusal={refusal}
            currency={currency}
            timezone={timezone}
            t={t}
            locale={locale}
          />
        </article>
      )}

      {!parcels.loading && rows.length === 0 ? (
        <p className="empty">{t('parcels.none')}</p>
      ) : (
        <div className="lanes">
          {LANES.map((lane) => {
            const inLane = rows.filter((parcel) => parcel.status === lane.status);
            return (
              <section key={lane.status} className={`lane lane--${lane.status}`}>
                <h2 className="lane__head">
                  {t(lane.label)} <span className="lane__count">{inLane.length}</span>
                </h2>
                {inLane.length === 0 ? (
                  <p className="lane__empty muted">—</p>
                ) : (
                  <ul className="lane__list">
                    {inLane.map((parcel) => (
                      <li key={parcel.order_id} className="ticket ticket--parcel">
                        <div className="ticket__open">
                          <strong>{parcel.recipient.name}</strong>
                          <small>
                            {t('parcels.itemsAndTotal', {
                              count: parcel.items.reduce((sum, item) => sum + item.quantity, 0),
                              total: formatMoney(parcel.total_cents, currency, locale),
                            })}
                          </small>
                          <small className="muted">
                            {t('parcels.placedAt', {
                              clock: formatClock(parcel.placed_at, timezone, locale),
                            })}
                            {parcel.shipping_service === '' ? '' : ` · ${parcel.shipping_service}`}
                          </small>
                          {parcel.provider_order_id === null ? null : (
                            <small className="muted">
                              {t('parcels.providerRef', {
                                provider: parcel.provider,
                                ref: parcel.provider_order_id,
                                draft: !parcel.confirmed,
                              })}
                            </small>
                          )}
                          {delivered.refusal !== null &&
                          delivered.refusal.orderId === parcel.order_id ? (
                            <p className="error ticket__refusal" role="alert">
                              {delivered.refusal.message}
                            </p>
                          ) : null}
                          {parcel.tracking.shipments.length === 0 ? null : (
                            <ul className="ticket__tracking">
                              {parcel.tracking.shipments.map((shipment) => (
                                <li key={`${shipment.carrier}|${shipment.tracking_number}`}>
                                  {shipment.tracking_url === '' ? (
                                    <span>
                                      {shipment.carrier} {shipment.tracking_number}
                                    </span>
                                  ) : (
                                    <a href={shipment.tracking_url} target="_blank" rel="noopener">
                                      {shipment.carrier} {shipment.tracking_number}
                                    </a>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        {lane.status === 'shipped' ? (
                          <div className="row-actions">
                            <button
                              type="button"
                              className="link-button"
                              disabled={delivered.busy === parcel.order_id}
                              onClick={() => void delivered.deliver(parcel)}
                            >
                              {t('parcels.markDelivered')}
                            </button>
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}

      {arrived.length === 0 ? null : (
        <article className="panel panel--muted">
          <h2>{t('parcels.deliveredHeading')}</h2>
          <ul className="lane__list lane__list--cancelled">
            {arrived.map((parcel) => (
              <li key={parcel.order_id} className="ticket ticket--cancelled">
                <div className="ticket__open">
                  <strong>{parcel.recipient.name}</strong>
                  <small>{formatMoney(parcel.total_cents, currency, locale)}</small>
                </div>
              </li>
            ))}
          </ul>
        </article>
      )}
    </section>
  );
}
